import { useState, useEffect, useMemo } from 'react';
import {
  useTable,
  useResizeColumns,
  useFlexLayout,
  useRowSelect,
  usePagination,
  useSortBy,
  useFilters,
  useGroupBy,
  useExpanded
} from 'react-table';
import { Link } from 'react-router-dom';
import config from '../../config/config';
import axios from 'axios';
import { matchSorter } from 'match-sorter';
import './Table.css';
import DownloadButton from './DownloadButton';
import { parseGroupedRows } from '../../utilities/tables';

export const AdminTable = ({ columns }) => {
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const fileName = 'ContactInfo';
  const affiliation_headers = [
    { label: 'Contact ID', key: 'contact_id' },
    { label: 'Affiliation', key: 'affiliation' },
    { label: 'Name', key: 'name' },
    { label: 'Title', key: 'title' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Phone Ext', key: 'ext' },
    { label: 'Address 1', key: 'address_1' },
    { label: 'Address 2', key: 'address_2' },
    { label: 'City', key: 'city' },
    { label: 'State', key: 'state' },
    { label: 'Zipcode', key: 'zipcode' },
    { label: 'Region Lead', key: 'region_lead' },
    { label: 'Counties', key: 'counties' },
    { label: 'HUCs', key: 'hucs' },
    { label: 'Study Areas', key: 'study_areas' },
    { label: 'Mitigation Areas', key: 'mitigation_areas' },
    { label: 'Outreach Types', key: 'outreach_types' },
    { label: 'Start Date', key: 'startdate' },
    { label: 'End Date', key: 'enddate' },
    { label: 'Term End Date', key: 'term_enddate' },
    { label: 'Elected', key: 'elected' },
    { label: 'Notes', key: 'notes' }
  ];

  async function getData(ender) {
    await axios
      .get(config.apiURL + ender, {
        headers: {
          'Content-Type': 'application/json',
          token: config.getToken()
        }
      })
      .then((response) => {
        setData(
          response.data.map((x) => {
            const keys = Object.keys(x);
            const entries = keys.map((key) => {
              if (
                typeof x[key] === 'object' &&
                x[key]?.hasOwnProperty('String')
              ) {
                return [key, x[key].String];
              } else {
                return [key, x[key]];
              }
            });
            const test = Object.fromEntries(entries);

            return test;
          })
        );
        setLoadingData(false);
      });
  }

  useEffect((loadingData) => {
    let all_end = '/contacts';
    getData(all_end);

    if (loadingData) {
      //if the result is not ready so you make the axios call
      getData(all_end);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      }
    }),
    []
  );

  // Define a default UI for filtering
  function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter }
  }) {
    const count = preFilteredRows.length;

    return (
      <input
        value={filterValue || ''}
        onChange={(e) => {
          setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
        }}
        style={{
          width: '100%',
          backgroundColor: 'white',
          color: 'black'
        }}
        placeholder={`Search ${count} records...`}
      />
    );
  }

  function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
  }

  // Let the table remove the filter if the string is empty
  fuzzyTextFilterFn.autoRemove = (val) => !val;

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  );

  //Table Instance
  const tableInstance = useTable(
    {
      columns,
      data,
      stateReducer: (newState, action) => {
        if (action.type === 'toggleRowSelected') {
          newState.selectedRowIds = {
            [action.id]: true
          };
        }
        return newState;
      },
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: {
        pageIndex: 0,
        groupBy: ['name']
      }
    },
    useFilters, // useFilters!
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useFlexLayout,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: 'selection',
          Header: 'Update',
          accessor: ['affiliation_id', 'contact_id', 'poc_id'],
          disableFilters: true,
          filterable: false,
          canSort: false,
          disableSort: true,
          disableSortBy: true,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => {
            if (row.subRows.length === 1) {
              return (
                <div>
                  <div>
                    <span {...row.getToggleRowExpandedProps()}>
                      {row.isExpanded ? '👇' : '👉'} ({row.subRows.length})
                    </span>
                  </div>
                  <Link
                    to={`/updateContact/${row.original.affiliation_id}/${row.original.contact_id}/${row.original.poc_id}`}
                    style={{ color: 'blue' }}
                  >
                    <button
                      className='btn'
                      style={{ margin: '2px', width: '80px', fontSize: '10px' }}
                      title='Click here to update the existing record'
                    >
                      Update
                    </button>
                  </Link>
                  <br />
                  <Link
                    to={`/addNewRecord/${row.original.affiliation_id}/${row.original.contact_id}/${row.original.poc_id}`}
                    style={{ color: 'blue' }}
                  >
                    <button
                      className='btn'
                      style={{ margin: '2px', width: '80px', fontSize: '10px' }}
                      title='Click here to add a new contact for this Person'
                    >
                      New Contact for this Person
                    </button>
                  </Link>
                </div>
              );
            }

            if (row.subRows.length > 1) {
              return (
                <div>
                  <span {...row.getToggleRowExpandedProps()}>
                    {row.isExpanded ? '👇' : '👉'} ({row.subRows.length})
                  </span>
                </div>
              );
            }
            if (row?.original) {
              return (
                <div>
                  <Link
                    to={`/updateContact/${row.original.affiliation_id}/${row.original.contact_id}/${row.original.poc_id}`}
                    style={{ color: 'blue' }}
                  >
                    <button
                      className='btn'
                      style={{ margin: '2px', width: '80px', fontSize: '10px' }}
                      title='Click here to update the existing record'
                    >
                      Update
                    </button>
                  </Link>
                  <br />
                  <Link
                    to={`/addNewRecord/${row.original.affiliation_id}/${row.original.contact_id}/${row.original.poc_id}`}
                    style={{ color: 'blue' }}
                  >
                    <button
                      className='btn'
                      style={{ margin: '2px', width: '80px', fontSize: '10px' }}
                      title='Click here to add a new contact for this Person'
                    >
                      New Contact
                    </button>
                  </Link>
                </div>
              );
            }

            return null;
          }
        },
        ...columns
      ]);
    }
  );

  //Set table variables
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page, //Begins the pagination and select stuff
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = tableInstance;

  const generateEmailList = () => {
    const emailList = new Set(parseGroupedRows(rows).map((row) => row.email));
    let win = window.open('', '_blank');
    win.document.write([...emailList].join(';<br>'));
  };

  return (
    <div style={{ flexGrow: '8', marginLeft: '10px', marginRight: '5px' }}>
      {loadingData ? (
        <p>Loading Please wait...</p>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    <div {...column.getSortByToggleProps()}>
                      {column.render('Header')}
                      <span>
                        {column.canSort ? (
                          column.isSorted ? (
                            column.isSortedDesc ? (
                              <span>&#9207;</span>
                            ) : (
                              <span>&#9206;</span>
                            )
                          ) : (
                            <span>&#8645;</span>
                          )
                        ) : (
                          <></>
                        )}
                      </span>
                    </div>
                    {column.canFilter ? column.render('Filter') : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);

              if (row.subRows.length === 1) {
                prepareRow(row.subRows[0]);
                return (
                  <tr {...row.subRows[0].getRowProps()}>
                    {row.subRows[0].cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              } else {
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, idx) => {
                      return (
                        <td {...cell.getCellProps()}>
                          {cell.isGrouped ? (
                            <>{cell.render('Cell')}</>
                          ) : (
                            <>
                              {cell.row.canExpand === false &&
                                idx === 0 &&
                                '>>'}
                              {cell.row.canExpand &&
                              cell.column.id === 'elected' ? (
                                <></>
                              ) : (
                                <>{cell.render('Cell')}</>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              }
            })}
          </tbody>
        </table>
        </div>
      )}
      <div className='pagination'>
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'<<'}
        </button>
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </button>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </button>
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {'>>'}
        </button>
        <span>
          Page
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <span>
          | Go to page:
          <input
            type='number'
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            style={{ width: '100px' }}
          />
        </span>
        <br />
        <button
          className='btn'
          style={{ margin: '10px', width: '150px', cursor: 'pointer' }}
          onClick={generateEmailList}
        >
          Generate Email List
        </button>
      </div>
      <DownloadButton
        new_data={rows}
        headers={affiliation_headers}
        file={fileName}
        dataType='react-table'
      />
    </div>
  );
};
